import React from 'react';
import { AnyAction } from 'redux';
import { ListItem } from '../../../../search/search-model';
import { UnitedStatesCountryCode } from '../../constants/constant-strings';

export interface PersonSearchAction extends AnyAction {
	key: PersonSearchKeys;
}

export enum PersonSearchActionTypes {
	SET_RESULTS = 'SET_RESULTS',
	SET_TOAST = 'SET_TOAST',
}

export enum OSCRRKeys {
	first_name = 'first_name',
	last_name = 'last_name',
	tax_id = 'tax_id',
	date_of_birth = 'date_of_birth',
	personal_email_address = 'personal_email_address',
	office_email_address = 'office_email_address',
	mailing_city = 'mailing_city',
	mailing_state = 'mailing_state',
	mailing_postal_code = 'mailing_postal_code',
	mailing_country = 'mailing_country',
}

export interface PersonSearchParams {
	first_name?: string;
	last_name?: string;
	ssn?: string;
	date_of_birth?: string;
	email_address?: string;
	city?: string;
	state?: string;
	postal_code?: string;
	country?: string;
	email?: string;
}

export interface OSCRRModel {
	[OSCRRKeys.first_name]?: string;
	[OSCRRKeys.last_name]?: string;
	[OSCRRKeys.tax_id]?: string;
	[OSCRRKeys.date_of_birth]?: string;
	[OSCRRKeys.personal_email_address]?: string;
	[OSCRRKeys.office_email_address]?: string;
	[OSCRRKeys.mailing_city]?: string;
	[OSCRRKeys.mailing_state]?: string;
	[OSCRRKeys.mailing_postal_code]?: string;
	[OSCRRKeys.mailing_country]?: string;
}

export const OSCRRRequiredKeys = new Set([
	OSCRRKeys.first_name.toString(),
	OSCRRKeys.last_name.toString(),
	OSCRRKeys.tax_id.toString(),
	OSCRRKeys.date_of_birth.toString(),
	OSCRRKeys.office_email_address.toString(),
	OSCRRKeys.personal_email_address.toString(),

]);

export const OSCRRKeySet = new Set([
	OSCRRKeys.first_name.toString(),
	OSCRRKeys.last_name.toString(),
	OSCRRKeys.tax_id.toString(),
	OSCRRKeys.date_of_birth.toString(),
	OSCRRKeys.personal_email_address.toString(),
	OSCRRKeys.office_email_address.toString(),
	OSCRRKeys.mailing_city.toString(),
	OSCRRKeys.mailing_state.toString(),
	OSCRRKeys.mailing_postal_code.toString(),
	OSCRRKeys.mailing_country.toString(),
]);

export interface PersonSearchPayload {
	search: OSCRRModel;
}

export enum PersonSearchKeys {
	None,
	MaritalStatus = 'MaritalStatus',
	Dependents = 'Dependents',
	GroupAdminAdd = 'GroupAdminAdd',
	OfficerAdd = 'OfficerAdd',
	NewHire = 'NewHire',
	DomesticPartner = 'DomesticPartner',
}

export interface SearchPersonAddress {
	id: number;
	address1: string;
	masked_address1: string;
	address2: string;
	masked_address2: string;
	address3: string;
	masked_address3: string;
	city: string;
	state: string;
	postal_code: string;
	country: string;
	errors: any;
	deactivated: boolean;
}

export interface SearchPersonEmail {
	id: number;
	email_address: string;
	errors: any;
}

export interface SearchPersonPhoneNumber {
	id: number;
	phone_country_code: string;
	phone_number_combined: string;
	phone_extension: string;
	errors: any;
}

export interface SearchPerson extends ListItem {
	guid: string;
	gender: string;
	client_number: string;
	masked_client_number: string;
	designation: string;
	salutation: string;
	first_name: string;
	middle_name: string;
	last_name: string;
	last_name_at_birth: string;
	full_name: string;
	ssn: string;
	masked_ssn: string;
	suffix: string;
	adoption_date: string;
	date_of_birth: string;
	masked_date_of_birth: string;
	date_of_death: string;
	errors: any;
	name: string;
	clergy: boolean;
	adopted: boolean;
	street_address: string;
	masked_street_address: string;
	location_address: string;
	country: string;
	mailing_address: SearchPersonAddress;
	personal_email: SearchPersonEmail;
	office_email: SearchPersonEmail;
	business_number: SearchPersonPhoneNumber;
	mobile_number: SearchPersonPhoneNumber;
	home_number: SearchPersonPhoneNumber;
}

export interface PersonSearchState {
	searchResults: SearchPerson[];
	hideToast: boolean;
}

export interface PersonSearchAppState {
	[PersonSearchKeys.MaritalStatus]: PersonSearchState;
	[PersonSearchKeys.Dependents]: PersonSearchState;
	[PersonSearchKeys.GroupAdminAdd]: PersonSearchState;
	[PersonSearchKeys.OfficerAdd]: PersonSearchState;
	[PersonSearchKeys.NewHire]: PersonSearchState;
	[PersonSearchKeys.DomesticPartner]: PersonSearchState;
}

export const PersonSearchContext = React.createContext(PersonSearchKeys.None);

// used to determine if fields should trigger person search
export const PerformPersonSearchContext = React.createContext(false);

export const TestSearchPerson = {
	id: 1,
	gender: 'Male',
	client_number: '',
	designation: 'Reverend',
	salutation: 'Mr.',
	first_name: 'John',
	middle_name: '',
	last_name: 'Doe',
	last_name_at_birth: '',
	full_name: 'John Doe',
	ssn: '111-22-3333',
	suffix: '',
	adoption_date: '',
	date_of_birth: '1977-01-01',
	date_of_death: '',
	name: '',
	clergy: false,
	adopted: false,
	street_address: '',
	location_address: '',
	country: 'US',
	mailing_address: {
		address1: '1500 Sandy Way',
		address2: 'Apt 3',
		city: 'Reno',
		state: 'NV',
		postal_code: '98229',
		country: 'US',
	} as SearchPersonAddress,
	personal_email: {
		email_address: 'test@test.com',
	} as SearchPersonEmail,
	office_email: {} as SearchPersonEmail,
	business_number: {
		phone_country_code: UnitedStatesCountryCode,
		phone_number_combined: '360-201-9999',
		phone_extension: '',
	} as SearchPersonPhoneNumber,
	mobile_number: {} as SearchPersonPhoneNumber,
	home_number: {} as SearchPersonPhoneNumber,
	errors: {},
};